import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import FormField from '../ui/FormField';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Textarea from '../ui/Textarea';
import Callout from '../ui/Callout';
import { useT } from '../../i18n/LocaleContext';

const emptyForm = { busId: '', departureLocationId: '', departureTime: '', notes: '' };

/** Assigns one bus to a pilgrimage at a departure location. */
export default function AssignmentFormModal({ isOpen, onClose, onSubmit, eventName, buses, locations, assignedBusIds }) {
  const t = useT();
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm(emptyForm);
    setErrors({});
  }, [isOpen]);

  // A bus can only be assigned once per pilgrimage, and only active ones count.
  const busOptions = useMemo(
    () => buses
      .filter((b) => b.active && !assignedBusIds.has(b.id))
      .map((b) => ({
        value: b.id,
        label: `${b.plateNumber}${b.name ? ` · ${b.name}` : ''} — ${t('transport_page.seats_count', { count: b.capacity })}`,
      })),
    [buses, assignedBusIds, t]
  );

  const locationOptions = useMemo(
    () => locations
      .filter((l) => l.active)
      .map((l) => ({ value: l.id, label: l.departureTime ? `${l.name} (${l.departureTime})` : l.name })),
    [locations]
  );

  const set = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      // Pre-fill the location's usual departure time when picking it.
      if (field === 'departureLocationId' && !prev.departureTime) {
        const loc = locations.find((l) => l.id === value);
        if (loc?.departureTime) next.departureTime = loc.departureTime;
      }
      return next;
    });
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!form.busId) next.busId = t('validation.required');
    if (!form.departureLocationId) next.departureLocationId = t('validation.required');
    setErrors(next);
    if (Object.keys(next).length) return;
    setSubmitting(true);
    try {
      await onSubmit({
        busId: form.busId,
        departureLocationId: form.departureLocationId,
        departureTime: form.departureTime || null,
        notes: form.notes.trim() || null,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? () => {} : onClose}
      title={t('transport_page.assign_bus')}
      subtitle={eventName}
      size="md"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>{t('common.cancel')}</Button>
          <Button variant="gold" onClick={handleSubmit} loading={submitting} disabled={!busOptions.length || !locationOptions.length}>
            {t('transport_page.assign')}
          </Button>
        </>
      )}
    >
      <form onSubmit={handleSubmit} noValidate>
        {!busOptions.length && <Callout tone="warning">{t('transport_page.no_free_bus')}</Callout>}
        {!locationOptions.length && <Callout tone="warning">{t('transport_page.no_active_location')}</Callout>}
        <FormField label={t('transport_page.field_bus')} htmlFor="asg-bus" required error={errors.busId}>
          <Select id="asg-bus" options={busOptions} placeholder={t('common.select_placeholder')} value={form.busId} onChange={set('busId')} invalid={Boolean(errors.busId)} />
        </FormField>
        <FormField label={t('transport_page.field_location')} htmlFor="asg-location" required error={errors.departureLocationId}>
          <Select id="asg-location" options={locationOptions} placeholder={t('common.select_placeholder')} value={form.departureLocationId} onChange={set('departureLocationId')} invalid={Boolean(errors.departureLocationId)} />
        </FormField>
        <FormField label={t('transport_page.field_departure_time')} htmlFor="asg-time" hint={t('common.optional')}>
          <Input id="asg-time" type="time" value={form.departureTime} onChange={set('departureTime')} />
        </FormField>
        <FormField label={t('common.notes')} htmlFor="asg-notes" hint={t('common.optional')}>
          <Textarea id="asg-notes" value={form.notes} onChange={set('notes')} rows={2} />
        </FormField>
      </form>
    </Modal>
  );
}
